import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MoreVertical } from 'lucide-react'
import { TimerPreset } from '../lib/useTimerPresets'

interface PresetGridProps {
	presets: TimerPreset[]
	onSelect: (preset: TimerPreset) => void
	isActive: (preset: TimerPreset) => boolean
	formatSeconds: (seconds: number) => string
}

interface PresetSlot {
	key: string
	preset: TimerPreset | null
}

export const PresetGrid: FC<PresetGridProps> = ({ presets, onSelect, isActive, formatSeconds }) => {
	const slots: PresetSlot[] = useMemo(
		() =>
			Array.from({ length: 4 }, (_, index) => ({
				preset: presets[index] ?? null,
				key: presets[index]
					? `${presets[index].rounds}-${presets[index].approach}-${presets[index].rest}-${presets[index].totalSeconds}`
					: `empty-${index}`,
			})),
		[presets]
	)
	const slotRefs = useRef<(HTMLButtonElement | null)[]>([])
	const [obscuredSlots, setObscuredSlots] = useState<boolean[]>(() => slots.map(() => false))

	const recomputeVisibility = useCallback(() => {
		if (typeof window === 'undefined') {
			return
		}

		const cssReserve = window
			.getComputedStyle(document.documentElement)
			.getPropertyValue('--app-bottom-nav-reserve')
		const reserve = Number.parseFloat(cssReserve || '') || 116
		const overlayTop = window.innerHeight - reserve + 10

		setObscuredSlots((previous) => {
			const next = slots.map((slot, index) => {
				if (!slot.preset) {
					return false
				}

				const node = slotRefs.current[index]
				if (!node) {
					return false
				}

				const rect = node.getBoundingClientRect()
				return rect.bottom > overlayTop
			})

			if (
				previous.length === next.length &&
				previous.every((previousValue, index) => previousValue === next[index])
			) {
				return previous
			}

			return next
		})
	}, [slots])

	useEffect(() => {
		recomputeVisibility()
		const timer = window.setInterval(recomputeVisibility, 350)
		window.addEventListener('resize', recomputeVisibility)
		window.addEventListener('scroll', recomputeVisibility, true)

		return () => {
			window.clearInterval(timer)
			window.removeEventListener('resize', recomputeVisibility)
			window.removeEventListener('scroll', recomputeVisibility, true)
		}
	}, [recomputeVisibility])

	return (
		<div className='mt-4 w-full pb-4'>
			<div className='mb-2 text-left text-[14px] font-medium text-[#252525]'>
				Сохранённые таймеры
			</div>
			<div className='grid grid-cols-2 gap-2'>
				{slots.map((slot, index) => {
					const preset = slot.preset
					const isObscured = Boolean(obscuredSlots[index])

					if (!preset) {
						return (
							<button
								type='button'
								key={slot.key}
								data-testid='timer-preset-slot'
								ref={node => {
									slotRefs.current[index] = node
								}}
								disabled
								className='flex min-h-[100px] flex-col rounded-[16px] border border-dashed border-[#DCDCDC] px-4 py-3 text-left text-[#C0C0C0]'>
								<span className='text-[9px] uppercase tracking-[0.2em]'>Пусто</span>
								<span className='mt-2 text-[14px] font-bold'>--:--</span>
								<div className='mt-auto text-[11px]'>—</div>
							</button>
						)
					}

					return (
						<button
							type='button'
							key={slot.key}
							data-testid='timer-preset-slot'
							ref={node => {
								slotRefs.current[index] = node
							}}
							onClick={() => onSelect(preset)}
							disabled={isObscured}
							aria-disabled={isObscured}
							data-obscured={isObscured}
							className={`flex min-h-[100px] flex-col rounded-[16px] border border-dashed px-4 py-3 text-left transition ${
								isActive(preset)
									? 'border-[#7F86D3] bg-[#F7F5FF]'
									: 'border-[#DCDCDC] bg-white'
							} ${isObscured ? 'pointer-events-none opacity-50' : 'opacity-100'}`}>
							<div className='flex items-start justify-between'>
								<span className='text-[18px] font-semibold leading-tight text-[#636AF8]'>
									{preset.rounds} Раундов
								</span>
								<MoreVertical aria-hidden='true' className='size-5 text-[#888]' />
							</div>
							<div className='mt-auto flex items-center gap-3 text-[14px] text-[#3F3F3F]'>
								<span>{preset.approach}с</span>
								<span className='text-[#A6A6A6]'>|</span>
								<span>{preset.rest}с</span>
								<span className='ml-auto font-semibold text-[#636AF8]'>
									{formatSeconds(preset.totalSeconds)}
								</span>
							</div>
						</button>
					)
				})}
			</div>
		</div>
	)
}
